import React from "react";
import {
  Button,
  ActionIcon,
  CopyButton,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { IconCopy, IconCheck } from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";

interface CopyBookingLinkButtonProps {
  bookingLink: string;
  variant?: "button" | "icon";
  size?: "sm" | "md" | "lg";
  color?: string;
  className?: string;
  timeout?: number;
}

/**
 * Reusable component for copying booking links
 * Supports both button and icon-only variants with responsive behavior
 */
export default function CopyBookingLinkButton({
  bookingLink,
  variant,
  size = "sm",
  color = "var(--brand-teal)",
  className,
  timeout = 2000,
}: CopyBookingLinkButtonProps) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  // Auto-detect variant based on screen size if not specified
  const effectiveVariant = variant || (isMobile ? "icon" : "button");

  if (!bookingLink) {
    return null;
  }

  return (
    <CopyButton value={bookingLink} timeout={timeout}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? "Copied!" : "Copy booking link"} withArrow>
          {effectiveVariant === "icon" ? (
            <ActionIcon
              variant="filled"
              color={color}
              size={size === "sm" ? "lg" : size}
              onClick={copy}
              className={className}
            >
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          ) : (
            <Button
              variant="filled"
              color={color}
              size={size}
              rightSection={
                copied ? <IconCheck size={16} /> : <IconCopy size={16} />
              }
              onClick={copy}
              className={className}
            >
              {copied ? "Copied!" : "Copy Booking Link"}
            </Button>
          )}
        </Tooltip>
      )}
    </CopyButton>
  );
}
